import React, { useState } from 'react';
import { 
  Mail, 
  Code, 
  Eye, 
  Save, 
  X, 
  FileText,
  Variable,
  Sparkles
} from 'lucide-react';

interface EmailTemplate {
  id: string;
  code: string;
  name: string;
  subject: string;
  body: string;
  variables: string[];
  category?: string;
}

interface EmailTemplateEditorProps {
  template: EmailTemplate;
  onSave: (template: EmailTemplate) => void;
  onClose: () => void;
}

export const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  template,
  onSave,
  onClose
}) => {
  const [editedTemplate, setEditedTemplate] = useState<EmailTemplate>(template);
  const [previewMode, setPreviewMode] = useState(false);
  const [sampleData, setSampleData] = useState<Record<string, string>>(() => {
    // Generuj przykładowe dane dla zmiennych
    const data: Record<string, string> = {};
    template.variables.forEach(variable => {
      switch (variable) {
        case 'offer_number':
          data[variable] = 'OF-2025-0123';
          break;
        case 'client_name':
          data[variable] = 'Jan Kowalski';
          break;
        case 'days_left':
          data[variable] = '3';
          break;
        case 'total_value':
          data[variable] = '12 500,00 zł';
          break;
        case 'expiry_date':
          data[variable] = '28.01.2025';
          break;
        case 'week_number':
          data[variable] = '4';
          break;
        case 'offers_count':
          data[variable] = '42';
          break;
        case 'month':
          data[variable] = 'styczeń 2025';
          break;
        default:
          data[variable] = `[${variable}]`;
      }
    });
    return data;
  });

  const handleVariableClick = (variable: string) => {
    // Wstaw zmienną w miejscu kursora
    const textarea = document.getElementById('template-body') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = editedTemplate.body;
      const newText = text.substring(0, start) + `{{${variable}}}` + text.substring(end);
      
      setEditedTemplate({ ...editedTemplate, body: newText });
      
      // Ustaw kursor po wstawionej zmiennej
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + variable.length + 4;
        textarea.focus();
      }, 0);
    }
  };

  const renderPreview = (text: string) => {
    let preview = text;
    Object.entries(sampleData).forEach(([key, value]) => {
      preview = preview.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    return preview;
  };

  const handleAddVariable = () => {
    const newVariable = prompt('Nazwa nowej zmiennej:');
    if (newVariable && !editedTemplate.variables.includes(newVariable)) {
      setEditedTemplate({
        ...editedTemplate,
        variables: [...editedTemplate.variables, newVariable]
      });
      setSampleData({
        ...sampleData,
        [newVariable]: `[${newVariable}]`
      });
    }
  };

  const handleRemoveVariable = (variable: string) => {
    setEditedTemplate({
      ...editedTemplate,
      variables: editedTemplate.variables.filter(v => v !== variable)
    });
    const newSampleData = { ...sampleData };
    delete newSampleData[variable];
    setSampleData(newSampleData);
  };

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-zinc-700">
        {/* Nagłówek */}
        <div className="p-6 border-b border-zinc-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Mail className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Edytor szablonu email</h2>
                <p className="text-sm text-gray-400">{editedTemplate.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Zawartość */}
        <div className="flex h-[calc(90vh-200px)]">
          {/* Panel edycji */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-4">
              {/* Nazwa szablonu */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nazwa szablonu
                </label>
                <input
                  type="text"
                  value={editedTemplate.name}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, name: e.target.value })}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Temat */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Temat wiadomości
                </label>
                <input
                  type="text"
                  value={editedTemplate.subject}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, subject: e.target.value })}
                  className="w-full px-4 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="np. Oferta {{offer_number}} wygasa za {{days_left}} dni"
                />
              </div>

              {/* Treść */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Treść wiadomości
                </label>
                <textarea
                  id="template-body"
                  value={editedTemplate.body}
                  onChange={(e) => setEditedTemplate({ ...editedTemplate, body: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono text-sm"
                  rows={15}
                  placeholder="Treść wiadomości..."
                />
              </div>

              {/* Przełącznik podglądu */}
              <div className="flex justify-end">
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    previewMode 
                      ? 'bg-orange-500 text-white' 
                      : 'bg-zinc-700 text-gray-300 hover:bg-zinc-600'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                  {previewMode ? 'Edycja' : 'Podgląd'}
                </button>
              </div>
            </div>
          </div>

          {/* Panel zmiennych */}
          <div className="w-80 bg-zinc-900 p-6 border-l border-zinc-700 overflow-y-auto">
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                <Variable className="w-4 h-4" />
                Zmienne
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Kliknij zmienną aby wstawić ją do szablonu
              </p>
            </div>

            <div className="space-y-2 mb-4">
              {editedTemplate.variables.map((variable) => (
                <div key={variable} className="flex items-center gap-2">
                  <button
                    onClick={() => handleVariableClick(variable)}
                    className="flex-1 px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-left text-sm font-mono text-orange-400 transition-colors"
                  >
                    {`{{${variable}}}`}
                  </button>
                  <button
                    onClick={() => handleRemoveVariable(variable)}
                    className="p-2 text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={handleAddVariable}
              className="w-full px-3 py-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-sm text-gray-300 transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Dodaj zmienną
            </button>

            {/* Przykładowe wartości */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-300 mb-3">
                Przykładowe wartości
              </h4>
              <div className="space-y-2">
                {Object.entries(sampleData).map(([key, value]) => (
                  <div key={key} className="text-xs">
                    <span className="text-gray-500">{key}:</span>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => setSampleData({ ...sampleData, [key]: e.target.value })}
                      className="ml-2 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white w-full mt-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Podgląd */}
        {previewMode && (
          <div className="absolute inset-0 bg-zinc-800 p-6 overflow-y-auto">
            <div className="max-w-2xl mx-auto">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Podgląd wiadomości</h3>
                <button
                  onClick={() => setPreviewMode(false)}
                  className="px-4 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors"
                >
                  Wróć do edycji
                </button>
              </div>
              
              <div className="bg-white rounded-lg p-6 text-gray-900">
                <div className="mb-4 pb-4 border-b">
                  <p className="text-sm text-gray-500">Temat:</p>
                  <p className="font-semibold">{renderPreview(editedTemplate.subject)}</p>
                </div>
                
                <div className="whitespace-pre-wrap">
                  {renderPreview(editedTemplate.body)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stopka */}
        <div className="p-6 border-t border-zinc-700 flex justify-between items-center">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Code className="w-4 h-4" />
            Kod szablonu: <span className="font-mono text-orange-400">{editedTemplate.code}</span>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-zinc-700 text-gray-300 rounded-lg hover:bg-zinc-700 transition-all"
            >
              Anuluj
            </button>
            <button
              onClick={() => onSave(editedTemplate)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all hover:scale-105"
            >
              <Save className="w-4 h-4" />
              Zapisz zmiany
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
